import { useEffect, useState } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { postComplianceReport, fetchComplianceFrameworks } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileDown, Info } from 'lucide-react';

interface FrameworkDef {
  id: string;
  name: string;
  color: string;
  status: string;
  /** Controls WE map, not the framework's own size. */
  total: number;
}

// Fallback only, used when the API is unreachable. Values match the evidence maps.
//
// This list used to be the sole source and carried a per-framework implemented /
// designed / partial / gap breakdown -- SOC 2 "61 total, 44 implemented", ISO 27001
// "93 controls" -- that no enumeration in the repository produced. 93 is ISO
// 27001:2022 Annex A's own total, not our mapped count, and presenting a fabricated
// split beneath a readiness disclaimer is worse than presenting nothing. The view now
// reports only what can be derived: how many controls are mapped, and at what status.
const FRAMEWORKS: FrameworkDef[] = [
  { id: 'soc2', name: 'SOC 2 Trust Services Criteria', color: '#60A5FA', status: 'READINESS', total: 43 },
  { id: 'eu-ai-act', name: 'EU AI Act (High-Risk AI Systems)', color: '#D4825F', status: 'READINESS', total: 8 },
  { id: 'hipaa', name: 'HIPAA Security Rule', color: '#34D399', status: 'READINESS', total: 15 },
  { id: 'iso-27001', name: 'ISO 27001:2022', color: '#FBBF24', status: 'READINESS', total: 15 },
  { id: 'pci-dss', name: 'PCI DSS', color: '#F87171', status: 'READINESS', total: 12 },
  { id: 'gdpr', name: 'GDPR', color: '#A78BFA', status: 'READINESS', total: 14 },
  { id: 'nist-800-53', name: 'NIST SP 800-53 Rev. 5', color: '#2DD4BF', status: 'READINESS', total: 15 },
  { id: 'mitre-atlas', name: 'MITRE ATLAS', color: '#F472B6', status: 'READINESS', total: 11 },
  { id: 'nist-ai-rmf', name: 'NIST AI RMF', color: '#4ADE80', status: 'READINESS', total: 29 },
  { id: 'nist-pqc', name: 'NIST PQC Migration Readiness', color: '#38BDF8', status: 'PLANNED', total: 44 },
  { id: 'sox', name: 'SOX', color: '#FB923C', status: 'PLANNED', total: 9 },
  { id: 'ferpa', name: 'FERPA', color: '#E879F9', status: 'PLANNED', total: 6 },
];

/** Palette by framework id, so a colour is not something the API has to supply. */
const FRAMEWORK_COLORS: Record<string, string> = {
  'soc2': '#60A5FA', 'hipaa': '#34D399', 'eu-ai-act': '#D4825F', 'iso-27001': '#FBBF24',
  'pci-dss': '#F87171', 'gdpr': '#A78BFA', 'nist-800-53': '#2DD4BF', 'nist-pqc': '#38BDF8',
  'sox': '#FB923C', 'ferpa': '#E879F9', 'mitre-atlas': '#F472B6', 'nist-ai-rmf': '#4ADE80',
};

export function ComplianceView({ connected = false }: { connected?: boolean }) {
  const [generating, setGenerating] = useState<string | null>(null);
  const readinessLabel = connected ? 'Managed readiness' : 'Self-assessed readiness';

  // Sourced from GET /api/cc/compliance/frameworks rather than the hardcoded list this
  // view used to carry. That list held eight frameworks with control totals matching no
  // enumeration in the repository, while getAvailableFrameworks() reported twelve -- the
  // console and the API disagreed about how many frameworks the platform covers, and the
  // view could only ever show what the report route's frozen allowlist would accept.
  // FRAMEWORKS below is the fallback for an unreachable API, not a second source of truth.
  const [frameworks, setFrameworks] = useState<FrameworkDef[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchComplianceFrameworks().then((data: any) => {
      if (cancelled || !data || data.error || !Array.isArray(data.frameworks)) return;
      setFrameworks(data.frameworks.map((f: any) => ({
        id: f.id,
        name: f.name,
        color: FRAMEWORK_COLORS[f.id] || '#A8A29E',
        status: f.status,
        // controlCount is what we MAP. A framework still at PLANNED reports null.
        total: typeof f.controlCount === 'number' ? f.controlCount : 0,
      })));
    });
    return () => { cancelled = true; };
  }, []);

  const handleGenerate = async (fwId: string) => {
    setGenerating(fwId);
    const result = await postComplianceReport(fwId);
    setGenerating(null);
    if (result && !result.error) {
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agentomy-${fwId}-report-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Compliance</h2>

      {/* Readiness disclaimer */}
      <div className="flex items-start gap-3 bg-[#292524] rounded-lg p-4 border" style={{ borderColor: `${GovernanceColors.warning}4D` }}>
        <Info className="w-5 h-5 shrink-0 mt-0.5" style={{ color: GovernanceColors.warning }} />
        <p className="text-xs text-[#A8A29E]">
          <span className="font-medium" style={{ color: GovernanceColors.warning }}>{readinessLabel}.</span>{' '}
          {connected
            ? 'These compliance readiness scores reflect managed policy enforcement across connected infrastructure. They indicate architectural readiness, not externally assessed compliance.'
            : 'These compliance readiness scores are based on internal self-assessment mapping. Connect a governance coordinator to enable managed readiness with cross-infrastructure policy enforcement.'}
        </p>
      </div>

      {/* Framework grid */}
      <div className="grid grid-cols-2 gap-4">
        {(frameworks ?? FRAMEWORKS).map((fw) => {
          const atReadiness = fw.status === 'READINESS';
          return (
            <Card key={fw.id} className="bg-[#292524] border-[#3A352F]">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-medium" style={{ color: fw.color }}>{fw.name}</h3>
                  <span
                    className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded shrink-0"
                    style={{
                      color: atReadiness ? GovernanceColors.healthy : GovernanceColors.warning,
                      border: `1px solid ${atReadiness ? GovernanceColors.healthy : GovernanceColors.warning}4D`
                    }}
                  >
                    {atReadiness ? 'Readiness' : 'Planned'}
                  </span>
                </div>
                {/* Controls MAPPED, not the framework's own size. No implemented/partial/gap
                    split is shown: the previous one was not derived from anything. */}
                <div className="flex items-baseline gap-2">
                  <span className="text-[#F5F0E8] font-medium text-lg">{fw.total > 0 ? fw.total : '--'}</span>
                  <span className="text-xs text-[#8F857B]">
                    {fw.total > 0 ? 'controls mapped to verified code' : 'control mapping in progress'}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-[#4A443D] text-[#A8A29E] hover:text-[#F5F0E8] hover:bg-[#3D3833] text-xs"
                  onClick={() => handleGenerate(fw.id)}
                  disabled={generating === fw.id}
                >
                  <FileDown className="w-3 h-3 mr-1" />
                  {generating === fw.id ? 'Generating...' : 'Generate Report'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
